import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { IMAGES, PERSON_SCHEMA } from "@/data/content";

const CHAPTERS = [
  {
    num: "01",
    title: "I miei primi 25 anni",
    text: [
      "Per 25 anni ho lavorato come dipendente. Anni fatti di orari, responsabilità, routine che si ripetono. Anni a cui devo molto: mi hanno insegnato la disciplina, il rispetto degli impegni, il valore del lavoro fatto bene.",
      "Non ho nulla contro quel percorso. Ma a un certo punto, dentro di me, è arrivata una domanda semplice e ostinata: «È tutto qui?». Non era insoddisfazione verso il lavoro. Era il desiderio di capire se la vita potesse essere anche qualcos'altro.",
    ],
  },
  {
    num: "02",
    title: "Il cambiamento",
    text: [
      "Non è successo da un giorno all'altro. Ho iniziato a cercare una strada diversa: mi sono informata, ho ascoltato storie, ho fatto domande, ho osservato chi aveva costruito qualcosa di suo.",
      "Il cambiamento, per me, non è stato un salto nel vuoto. È stato un percorso di piccoli passi consapevoli, fatto di studio e di scelte pensate. Non cercavo il successo garantito — non esiste. Cercavo una direzione che somigliasse di più alla vita che desidero.",
    ],
  },
  {
    num: "03",
    title: "La paura di iniziare",
    text: [
      "Videochiamate su Zoom, video da pubblicare, presentazioni davanti a un pubblico: per chi ha passato anni dall'altra parte, esporsi in prima persona è una montagna. Ricordo la mia prima presentazione: la voce che non usciva come volevo, le mani fredde, il cuore veloce.",
      "Poi ho capito che le persone davanti a me non erano lì per giudicarmi. Molte avevano vissuto la stessa paura. Quella presentazione non fu perfetta, ma fu fatta. E da lì, ogni volta è diventata un po' più facile.",
    ],
  },
  {
    num: "04",
    title: "Cosa sto imparando oggi",
    text: [
      "Oggi studio il digitale, la comunicazione, il mondo dei viaggi. Imparo a raccontarmi online, a costruire relazioni vere, a lavorare con strumenti che fino a pochi anni fa non conoscevo.",
      "Ogni giorno scopro qualcosa che non sapevo. E questa, lo ammetto, è la parte più bella.",
    ],
    quote: "Non ho smesso di imparare. Ho semplicemente iniziato a imparare cose nuove.",
  },
  {
    num: "05",
    title: "Cosa sto costruendo",
    text: [
      "Sto costruendo un percorso personale e professionale che unisce quattro mondi: viaggi, digitale, formazione e networking. Porto avanti idee che uniscono l'Italia e l'Albania, la terra delle mie radici.",
      "Una parte di questo percorso è aiutare altre persone a scoprire nuove possibilità: attraverso il viaggio e, per chi lo desidera, attraverso un'attività come quella che sto costruendo io. Nessuna promessa e nessun traguardo da esibire: è tutto in costruzione, fatto di direzione, costanza e libertà di farlo ai miei tempi.",
    ],
  },
];

export default function Storia() {
  return (
    <div data-testid="storia-page">
      <Seo
        title="Chi sono | Laure Hila — Viaggia in Grande"
        description="Chi è Laure Hila: dopo 25 anni da dipendente, la scelta di cambiare. Origini albanesi, vita in Italia, viaggi, imprenditoria e networking internazionale."
        jsonLd={PERSON_SCHEMA}
      />

      <section className="pt-32 md:pt-44 pb-16 md:pb-24 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">La mia storia</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Una vita che torna
            </MaskedLine>
            <MaskedLine delay={0.25} className="text-5xl md:text-7xl italic text-gold">
              a scegliere.
            </MaskedLine>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              Cinque capitoli, nessuna retorica. Quello che è successo davvero,
              raccontato con le parole più semplici che conosco.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative">
            <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-line md:-translate-x-1/2" />
            {CHAPTERS.map((c, i) => (
              <div
                key={c.num}
                data-testid={`storia-chapter-${c.num}`}
                className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-20 pb-16 md:pb-24 pl-14 md:pl-0 ${
                  i % 2 === 1 ? "" : ""
                }`}
              >
                <div className="absolute left-0 md:left-1/2 top-1 md:-translate-x-1/2 w-10 h-10 rounded-full bg-cream border border-gold flex items-center justify-center z-10">
                  <span className="font-serif text-sm text-gold font-semibold">{c.num}</span>
                </div>
                <Reveal className={i % 2 === 1 ? "md:order-2 md:text-left" : "md:text-right"}>
                  <h2 className="font-serif text-3xl md:text-4xl font-medium text-ink tracking-tight">
                    {c.title}
                  </h2>
                </Reveal>
                <Reveal delay={0.12} className={i % 2 === 1 ? "md:order-1" : ""}>
                  {c.text.map((p, j) => (
                    <p key={j} className="text-base md:text-lg leading-relaxed text-stone mb-4 last:mb-0">
                      {p}
                    </p>
                  ))}
                  {c.quote && (
                    <blockquote className="mt-6 pl-5 border-l-2 border-gold font-serif text-xl md:text-2xl italic text-ink">
                      «{c.quote}»
                    </blockquote>
                  )}
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="eyebrow mb-4 text-center">I ritratti di questo capitolo</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10 mt-8">
            <Reveal>
              <div className="frame-gold">
                <div className="relative z-10 overflow-hidden rounded-xl">
                  <img
                    src={IMAGES.proBianco.url}
                    alt={IMAGES.proBianco.alt}
                    style={{ objectPosition: "center 20%" }}
                    className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="sm:mt-16">
              <div className="overflow-hidden rounded-xl">
                <img
                  src={IMAGES.proCappotto.url}
                  alt={IMAGES.proCappotto.alt}
                  style={{ objectPosition: "center 14%" }}
                  className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden rounded-xl">
              <img
                src={IMAGES.shala.url}
                alt={IMAGES.shala.alt}
                style={{ objectPosition: "center 60%" }}
                className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">Le mie radici</p>
              <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-ink">
                Tra l'Italia e <span className="italic text-gold">l'Albania</span>
              </h2>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-stone">
                Sono di origine albanese e vivo in Italia da molti anni. Due
                terre, due culture, un unico modo di guardare il mondo: con
                curiosità.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-stone">
                Questo legame fa parte del mio modo di viaggiare, di incontrare
                le persone e di immaginare nuove connessioni — anche nel turismo
                e nel mio percorso professionale, che oggi include la membership
                in Skål International, la rete mondiale dei professionisti del
                turismo e dell'ospitalità.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={IMAGES.proPontile.url}
                alt={IMAGES.proPontile.alt}
                style={{ objectPosition: "center 48%" }}
                className="w-full aspect-[16/10] md:aspect-[16/8] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-ink/35 flex items-center justify-center px-6">
                <div className="text-center">
                  <p className="font-serif text-3xl md:text-5xl text-cream text-balance">
                    Se ti va di conoscermi meglio,{" "}
                    <span className="italic text-gold">parliamone</span>.
                  </p>
                  <Link
                    to="/contatti"
                    data-testid="storia-cta-contatti"
                    className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cream text-ink font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300 group"
                  >
                    Vai ai contatti
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
